<script setup lang="ts">
import { nextTick, watch } from 'vue'

import type { FineUploaderModalUploadHelpers } from '@/composables/current-student-advanced-standing-form/fineUploaderModalUploadHelpers'
import { useExternalSubjectModalController } from '@/composables/current-student-advanced-standing-form/useExternalSubjectModalController'
import { maxUploadAttachments } from '@/constants/current-student-advanced-standing-form/upload'
import {
  buildExternalSubjectDeleteRequest,
  buildExternalSubjectEditPayload,
  externalSubjectDeleteConfirmationMessage,
} from '@/models/current-student-advanced-standing-form/externalSubjectModal'
import {
  clearModalAlert,
  showModalError,
  showModalInfo,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  FineUploaderUpload,
  StudentEditSubmissionRequest,
  StudentEditSubmissionResponse,
  StudentFormSubmission,
} from '@/models/current-student-advanced-standing-form/api'
import ExternalSubjectModal from './ExternalSubjectModal.vue'

type ConfirmDeleteAction = () => Promise<void>

const props = defineProps<{
  secureData: string
  modalIsShown: (modalId: string) => boolean
  modalIsActive: (modalId: string) => boolean
  syncModalVisibility: (modalId: string, visible: boolean) => void
  editStudentSubmission: (payload: StudentEditSubmissionRequest) => Promise<StudentEditSubmissionResponse>
  setSubmission: (submission: StudentFormSubmission) => void
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
  openConfirmDelete: (message: string, action: ConfirmDeleteAction) => void
  uploadHelpers: FineUploaderModalUploadHelpers
  triggerUploadInput: (event: MouseEvent) => void
  uploadButtonKeydown: (event: KeyboardEvent) => void
  setUploadButtonHover: (event: MouseEvent, hover: boolean) => void
  setUploadButtonFocus: (event: FocusEvent, focused: boolean) => void
  cancelStagedUpload: (attachment: FineUploaderUpload) => void
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const {
  externalSubjectModalVisible,
  externalSubjectSaving,
  externalSubjectModalError,
  externalSubjectModalAlertTone,
  externalSubjectFormRef,
  currentExternalSubject,
  externalSubjectCode,
  externalSubjectName,
  externalSubjectYear,
  externalSubjectLevel,
  externalSubjectUrl,
  subjectAttachmentDeleteIds,
  subjectNewAttachments,
  subjectUploadsInProgress,
  subjectUploadDropActive,
  subjectUploadAlertMessage,
  currentSubjectAttachments,
  showSubjectFineUploader,
  visibleSubjectNewAttachments,
  openAddExternalSubjectModal,
  openEditExternalSubjectModal,
  openExternalSubjectModal,
  closeExternalSubjectModal,
  setExternalSubjectFormRef,
  onExternalSubjectCodeBeforeInput,
  closeSubjectUploadAlert,
  onSubjectUploadDragEnter,
  onSubjectUploadDragOver,
  onSubjectUploadDragLeave,
  onSubjectUploadDrop,
  onSubjectAttachmentSelected,
  stageSubjectAttachmentDelete,
  deleteStagedSubjectAttachment,
  retrySubjectAttachment,
} = useExternalSubjectModalController({
  maxAttachments: maxUploadAttachments,
  uploadHelpers: props.uploadHelpers,
})

watch(externalSubjectModalVisible, (visible) => props.syncModalVisibility('ExternalSubjectModal', visible))

async function saveExternalSubjectModal(): Promise<void> {
  if (externalSubjectSaving.value) return

  if (externalSubjectFormRef.value?.reportValidity() === false) {
    return
  }

  const externalCourseId = currentExternalSubject.value?.external_course_id
  if (externalCourseId == null) {
    showModalError(externalSubjectModalError, externalSubjectModalAlertTone, 'Could not determine the selected course for this subject.')
    return
  }

  if (subjectUploadsInProgress.value > 0) {
    showModalError(externalSubjectModalError, externalSubjectModalAlertTone, 'Please wait for upload to complete')
    return
  }

  externalSubjectSaving.value = true
  showModalInfo(externalSubjectModalError, externalSubjectModalAlertTone, 'Saving ...')

  try {
    const externalSubjectPayload = buildExternalSubjectEditPayload({
      existingSubject: currentExternalSubject.value,
      externalCourseId,
      deleteAttachmentIds: subjectAttachmentDeleteIds.value,
      newAttachments: subjectNewAttachments.value,
      externalSubjectCode: externalSubjectCode.value,
      externalSubjectLevel: externalSubjectLevel.value,
      externalSubjectName: externalSubjectName.value,
      externalSubjectYear: externalSubjectYear.value,
      externalSubjectUrl: externalSubjectUrl.value,
    })

    const result = await props.editStudentSubmission({
      secure_data: props.secureData,
      external_subject: externalSubjectPayload,
    })
    props.setSubmission(result.data.submission)
    clearModalAlert(externalSubjectModalError, externalSubjectModalAlertTone)
    closeExternalSubjectModal()
  } catch (error) {
    if (props.handleAuthSessionError(error, 'reload')) return

    showModalError(externalSubjectModalError, externalSubjectModalAlertTone, props.getErrorMessage(error))
    scrollToElementAfterRender('#externalSubjectModalError')
  } finally {
    externalSubjectSaving.value = false
  }
}

function confirmExternalSubjectDelete(): void {
  const externalSubjectId = currentExternalSubject.value?.external_subject_id
  if (externalSubjectId == null) return

  props.openConfirmDelete(
    externalSubjectDeleteConfirmationMessage(currentExternalSubject.value?.external_subject_code),
    async () => {
      const result = await props.editStudentSubmission(buildExternalSubjectDeleteRequest(props.secureData, externalSubjectId))
      props.setSubmission(result.data.submission)
      closeExternalSubjectModal()
    },
  )
}

function scrollToElementAfterRender(selector: string): void {
  void nextTick(() => {
    window.requestAnimationFrame(() => {
      document.querySelector(selector)?.scrollIntoView()
    })
  })
}

defineExpose({
  openAddExternalSubjectModal,
  openEditExternalSubjectModal,
  openExternalSubjectModal,
  closeExternalSubjectModal,
})
</script>

<template>
  <ExternalSubjectModal
    :shown="modalIsShown('ExternalSubjectModal')"
    :active="modalIsActive('ExternalSubjectModal')"
    v-model:external-subject-code="externalSubjectCode"
    v-model:external-subject-name="externalSubjectName"
    v-model:external-subject-year="externalSubjectYear"
    v-model:external-subject-level="externalSubjectLevel"
    v-model:external-subject-url="externalSubjectUrl"
    :current-external-subject="currentExternalSubject"
    :current-subject-attachments="currentSubjectAttachments"
    :subject-attachment-delete-ids="subjectAttachmentDeleteIds"
    :show-subject-fine-uploader="showSubjectFineUploader"
    :visible-subject-new-attachments="visibleSubjectNewAttachments"
    :subject-upload-drop-active="subjectUploadDropActive"
    :subject-upload-alert-message="subjectUploadAlertMessage"
    :error-message="externalSubjectModalError"
    :alert-tone="externalSubjectModalAlertTone"
    :saving="externalSubjectSaving"
    :form-ref="setExternalSubjectFormRef"
    :upload-thumbnail-url="uploadThumbnailUrl"
    @subject-code-before-input="onExternalSubjectCodeBeforeInput"
    @stage-subject-attachment-delete="stageSubjectAttachmentDelete"
    @subject-upload-drag-enter="onSubjectUploadDragEnter"
    @subject-upload-drag-over="onSubjectUploadDragOver"
    @subject-upload-drag-leave="onSubjectUploadDragLeave"
    @subject-upload-drop="onSubjectUploadDrop"
    @trigger-upload-input="triggerUploadInput"
    @upload-button-keydown="uploadButtonKeydown"
    @upload-button-hover="setUploadButtonHover"
    @upload-button-focus="setUploadButtonFocus"
    @subject-attachment-selected="onSubjectAttachmentSelected"
    @cancel-staged-upload="cancelStagedUpload"
    @retry-subject-attachment="retrySubjectAttachment"
    @delete-staged-subject-attachment="deleteStagedSubjectAttachment"
    @close-subject-upload-alert="closeSubjectUploadAlert"
    @delete="confirmExternalSubjectDelete"
    @close="closeExternalSubjectModal"
    @save="saveExternalSubjectModal"
  />
</template>
